import React, { useCallback, useContext } from "react";
import type { DropResult } from "react-beautiful-dnd";
import { commitMutation } from "react-relay";

import { graphql } from "relay-runtime";

import CreateActionItemGroupButton from "~/actionItems/actionItemGroup/CreateActionItemGroupButton";
import {
	GROUP_BY_CUSTOM,
	GROUP_DROPPABLE_TYPE,
	SORT_BY_CUSTOM,
	SORT_BY_DATE_CREATED,
	SORT_BY_DUE_DATE,
	Views,
} from "~/actionItems/constants";
import { NoteBlockStatus, StatusCondition } from "~/actionItems/enum";
import type { FilterType } from "~/actionItems/filters/Filters";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import type { ActionItem } from "~/actionItems/types";
import {
	createdAtSort,
	customWithGroupSort,
	dueDateSort,
	generateSyntheticGroup,
	isNotNull,
} from "~/actionItems/utils";
import between, { nowUtcMySqlFormat } from "~/lib/between";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { environment } from "~/models";

import ActionItemGroupsRenderer from "./ActionItemGroupsRenderer";
import {
	isGroupSyntheticAISuggestions,
	SYNTHETIC_AI_SUGGESTIONS_GROUP_NAME,
	useShouldShowAISuggestions,
	wasCreatedWithinLastNDays,
} from "./aiSuggestionsUtils";

import type { ActionItemGroupsListMoveActionItemMutation } from "~/__generated__/ActionItemGroupsListMoveActionItemMutation.graphql";
import type { ActionItemGroupsListMoveActionItemsMutation } from "~/__generated__/ActionItemGroupsListMoveActionItemsMutation.graphql";
import type { ActionItemGroupsListMoveMutation } from "~/__generated__/ActionItemGroupsListMoveMutation.graphql";
import type { ActionItemGroupsListQuery } from "~/__generated__/ActionItemGroupsListQuery.graphql";

// This needs to be updated whenever we add a new filter below, mapping the item arg name to
// the name of the field on the filters object
const availableFilters: Array<keyof FilterType> = [
	"showAIDetected",
	"showCompleted",
	"showWontDo",
	"showCreatedByYou",
	"showRecentOnly",
	"hideAI",
];
export const GROUP_ITEMS_KEY_REQUIRED_ARGS: Record<string, keyof Pick<FilterType, (typeof availableFilters)[number]>> =
	{
		includeAiDetected: "showAIDetected",
		includeCompleted: "showCompleted",
		includeWontDo: "showWontDo",
	};

// We use connections so we can manipulate manipulate the edges
// But we don't want to do pagination here for now
const actionItemGroupsQuery = graphql`
	query ActionItemGroupsListQuery(
		$cursor: String
		$count: Int = 1000
		$includeCompleted: Boolean
		$includeWontDo: Boolean
		$includeAiDetected: Boolean
	) {
		actionItemGroups(first: $count, after: $cursor) @connection(key: "ActionItemGroupsList_actionItemGroups") {
			edges {
				node {
					id
					name
					order
					isDefaultGroup
					items(
						includeCompleted: $includeCompleted
						includeWontDo: $includeWontDo
						includeAiDetected: $includeAiDetected
					) {
						edges {
							node {
								...ActionItemData @relay(mask: false)
							}
						}
					}
				}
			}
		}
	}
`;

const moveActionItemGroupMutation = graphql`
	mutation ActionItemGroupsListMoveMutation($groupId: ID!, $newOrder: Int!) @raw_response_type {
		moveCustomActionItemGroup(groupId: $groupId, newOrder: $newOrder) {
			actionItemGroups {
				id
				order
			}
		}
	}
`;

const moveActionItemMutation = graphql`
	mutation ActionItemGroupsListMoveActionItemMutation(
		$noteBlockId: ID!
		$groupId: ID!
		$orderInsertedBefore: String
		$orderInsertedAfter: String
		$includeCompleted: Boolean
		$includeWontDo: Boolean
		$includeAiDetected: Boolean
	) @raw_response_type {
		moveActionItemToGroup(
			noteBlockId: $noteBlockId
			groupId: $groupId
			orderInsertedBefore: $orderInsertedBefore
			orderInsertedAfter: $orderInsertedAfter
		) {
			updatedBlock {
				id
				userOrderWithinGroup
			}
			actionItemGroups {
				id
				items(
					includeCompleted: $includeCompleted
					includeWontDo: $includeWontDo
					includeAiDetected: $includeAiDetected
				) {
					edges {
						node {
							id
						}
					}
				}
			}
		}
	}
`;

const moveActionItemsMutation = graphql`
	mutation ActionItemGroupsListMoveActionItemsMutation(
		$noteBlockIds: [ID!]!
		$groupId: ID!
		$orderInsertedBefore: String
		$orderInsertedAfter: String
		$includeCompleted: Boolean
		$includeWontDo: Boolean
		$includeAiDetected: Boolean
	) @raw_response_type {
		moveActionItemsToGroup(
			noteBlockIds: $noteBlockIds
			groupId: $groupId
			orderInsertedBefore: $orderInsertedBefore
			orderInsertedAfter: $orderInsertedAfter
		) {
			updatedBlocks {
				id
				userOrderWithinGroup
			}
			actionItemGroups {
				id
				items(
					includeCompleted: $includeCompleted
					includeWontDo: $includeWontDo
					includeAiDetected: $includeAiDetected
				) {
					edges {
						node {
							id
						}
					}
				}
			}
		}
	}
`;

export default function ActionItemGroupsList() {
	const { filters, selectedActionItems, inPanel, view, viewer } = useContext(ActionItemsContext);
	const user = viewer?.user;
	const { groupBy, sortBy, showCompleted, showWontDo, showAIDetected, search } = filters;
	const inRecording = view === Views.RECORDING;

	const data = useLazyLoadQuery<ActionItemGroupsListQuery>(
		actionItemGroupsQuery,
		{
			includeCompleted: showCompleted,
			includeWontDo: showWontDo,
			includeAiDetected: showAIDetected,
		},
		{ fetchPolicy: "store-and-network" },
	);

	const shouldShowAiSuggestions = useShouldShowAISuggestions();

	// We use groupsDictionary to get data within the group when drag and dropping
	// Action items between groups
	const [groupsDictionary, groups] = React.useMemo(() => {
		let sortedGroups = data.actionItemGroups?.edges.filter(isNotNull).map(group => group.node) || [];
		const aiDetectedUnacceptedActionItems: Array<
			NonNullable<
				ActionItemGroupsListQuery["response"]["actionItemGroups"]["edges"][number]
			>["node"]["items"]["edges"][number]
		> = [];
		const newsGroupsDictionary: { [key: string]: number } = {};

		sortedGroups.sort((a, b) => a.order - b.order);
		let sortingFunction = customWithGroupSort;
		if (sortBy === SORT_BY_DUE_DATE) {
			sortingFunction = dueDateSort;
		} else if (sortBy === SORT_BY_DATE_CREATED) {
			sortingFunction = createdAtSort;
		}

		const actionItemsIdsSet = new Set();
		// We need to get all original item ids so we can later
		// Filter out non original block ids when the original block
		// Is already present
		sortedGroups.forEach(group => {
			group.items.edges.forEach(item => {
				if (!item?.node || item.node.hasOriginalBlock) return;
				actionItemsIdsSet.add(item?.node.id);
			});
		});

		sortedGroups = sortedGroups.map((group, index) => {
			newsGroupsDictionary[group.id] = index;
			const edges = group.items.edges.filter(item => {
				if (item?.node?.originalBlock?.id) {
					if (actionItemsIdsSet.has(item.node.originalBlock.id)) {
						return false;
					}
					actionItemsIdsSet.add(item.node.originalBlock.id);
				}

				// Shows action items without assignee or where the viewer is one of the assignees
				const viewCondition =
					!Boolean(item?.node?.assignments?.length) ||
					Boolean(item?.node.assignments?.some(assignment => assignment.assignee.id === user?.id));
				let completedForUser = item?.node?.done;
				let wontDoForUser = item?.node?.wontDo;
				if (item?.node?.statusCondition === StatusCondition.ALL && item.node.assignments) {
					for (let i = 0; i < item.node.assignments.length; i++) {
						if (item.node.assignments[i].assignee.id !== user?.id) continue;
						completedForUser = item.node.assignments[i].status === NoteBlockStatus.DONE;
						wontDoForUser = item.node.assignments[i].status === NoteBlockStatus.WONT_DO;
						break;
					}
				}
				const completedCondition = !showCompleted ? !completedForUser : true;
				const wontDoCondition = !showWontDo ? !wontDoForUser : true;
				const searchCondition = !search
					? true
					: item?.node?.text.toLowerCase().includes(search) ||
						item?.node?.note.stream.displayName.toLowerCase().includes(search);

				const shouldShowActionItem =
					isNotNull(item) &&
					isNotNull(item.node) &&
					completedCondition &&
					wontDoCondition &&
					searchCondition &&
					viewCondition;

				// For an AI-generated action item to show in the AI Suggestions section, it must:
				// - Not exist in another user-defined group (only exists in default "Inbox" group)
				// - Have been created in the last 2 weeks
				// - Be assigned to the current user (should be automatic in USER view)
				// - Not be done or archived
				if (shouldShowAiSuggestions && shouldShowActionItem && item?.node.aiGenerated && group.isDefaultGroup) {
					const createdWithinLastTwoWeeks = wasCreatedWithinLastNDays(item.node.createdAt, 14);

					// Only include items from the last 2 weeks.
					// Older unaccepted items will not be shown on the page at all.
					if (createdWithinLastTwoWeeks) {
						aiDetectedUnacceptedActionItems.push(item);
					}
					// We don't want action items in AI Suggestions to also appear in other groups,
					// so we return false here
					return false;
				}

				return shouldShowActionItem;
			});
			edges.sort(sortingFunction);
			return {
				...group,
				items: {
					edges,
				},
			};
		});

		const syntheticAISuggestionsGroup = shouldShowAiSuggestions
			? {
					...generateSyntheticGroup(SYNTHETIC_AI_SUGGESTIONS_GROUP_NAME),
					items: {
						edges: aiDetectedUnacceptedActionItems,
					},
				}
			: null;

		const aiSuggestionsIsShown = shouldShowAiSuggestions && syntheticAISuggestionsGroup?.items.edges.length;

		// Don't include AI Suggestions in the groups dictionary since it's not draggable and doesn't have a database order
		// Keep real groups at their natural 0-based indices to avoid index adjustment complexity
		return [newsGroupsDictionary, aiSuggestionsIsShown ? [syntheticAISuggestionsGroup, ...sortedGroups] : sortedGroups];
	}, [data, sortBy, showCompleted, showWontDo, search, user, shouldShowAiSuggestions]);

	// Check if AI suggestions are currently shown as the first group
	const aiSuggestionsShown = React.useMemo(
		() => shouldShowAiSuggestions && groups.length > 0 && isGroupSyntheticAISuggestions(groups[0]),
		[shouldShowAiSuggestions, groups],
	);

	const multiBlockMove = useCallback(
		(items: Array<ActionItem>, groupId: string, orderInsertedBefore?: string, orderInsertedAfter?: string) => {
			let optimisticGroups = groups.map(group => {
				return {
					id: group.id,
					items: {
						edges: group.items.edges.map(edge => {
							return {
								node: {
									id: edge?.node.id || "",
								},
							};
						}),
					},
				};
			});
			const targetGroupIndex = groupsDictionary[groupId];
			// Adjust index to account for AI suggestions group at array position 0
			const actualTargetGroupIndex = aiSuggestionsShown ? targetGroupIndex + 1 : targetGroupIndex;
			// We get the group orders for each item (we need for checking the current order of the selected items)
			const updatedItems = items.map(item => {
				let groupOrder = -1;
				let groupIndex = -1;
				let itemIndex = -1;
				for (let i = 0; i < groups.length; i++) {
					const index = groups[i].items.edges.findIndex(groupItem => groupItem?.node.id === item.id);
					if (index >= 0) {
						groupOrder = groups[i].order;
						groupIndex = i;
						itemIndex = index;
						break;
					}
				}
				if (groupOrder === -1) {
					console.error("Failed to find group order for one of the drag and drops");
				}
				return {
					node: item,
					groupOrder,
					groupIndex,
					itemIndex,
				};
			});
			// Sort the list so we order the items in the correct order
			updatedItems.sort((a, b) => {
				if (a.groupOrder > b.groupOrder) return 1;
				if (a.groupOrder < b.groupOrder) return -1;
				return customWithGroupSort(a, b);
			});
			// Update the optimistic groups
			// We run the list in the reversed order so when splicing we don't mess with the indexes
			for (let i = updatedItems.length - 1; i >= 0; i--) {
				// If the group didn't change we don't need to do anything
				if (updatedItems[i].groupIndex === actualTargetGroupIndex) {
					continue;
				}
				const addedEdge = {
					node: {
						id: updatedItems[i].node.id,
					},
				};
				optimisticGroups[updatedItems[i].groupIndex].items.edges.splice(updatedItems[i].itemIndex, 1);
				optimisticGroups[actualTargetGroupIndex].items.edges = [
					...optimisticGroups[actualTargetGroupIndex].items.edges,
					addedEdge,
				];
			}
			const ids = updatedItems.map(item => item.node.id);
			const updatedBlocks: Array<{ id: string; userOrderWithinGroup: string }> = [];
			let currentInsertedAfter = orderInsertedAfter;
			ids.forEach(id => {
				const newOrder = between(orderInsertedBefore || " ", currentInsertedAfter || nowUtcMySqlFormat());
				updatedBlocks.push({
					id,
					userOrderWithinGroup: newOrder,
				});
				currentInsertedAfter = newOrder;
			});

			// Moving action item
			commitMutation<ActionItemGroupsListMoveActionItemsMutation>(environment, {
				mutation: moveActionItemsMutation,
				variables: {
					noteBlockIds: ids,
					groupId,
					orderInsertedBefore,
					orderInsertedAfter,
					includeCompleted: showCompleted,
					includeWontDo: showWontDo,
					includeAiDetected: showAIDetected,
				},
				optimisticResponse: {
					moveActionItemsToGroup: {
						updatedBlocks,
						actionItemGroups: optimisticGroups,
					},
				},
			});
		},
		[groups, groupsDictionary, showCompleted, showWontDo, showAIDetected, aiSuggestionsShown],
	);

	const onDropEnd = useCallback(
		({ destination, draggableId, type, source }: DropResult) => {
			// Group by should never be not CUSTOM, but we check just in case
			// Also, it should be impossible to drag items into the AI Suggestions group
			if (
				!destination ||
				groupBy !== GROUP_BY_CUSTOM ||
				destination.droppableId === SYNTHETIC_AI_SUGGESTIONS_GROUP_NAME
			)
				return;

			// Prevent dragging the AI Suggestions group itself since it's synthetic
			const draggedGroup = groups.find(group => group.id === draggableId);
			if (draggedGroup && isGroupSyntheticAISuggestions(draggedGroup)) {
				return;
			}

			// Moving action item group
			if (type === GROUP_DROPPABLE_TYPE) {
				// Do not do anything if the order is not changing
				if (source.index === destination.index) return;

				// Convert drag indices to database order values (real groups maintain 0-based indices)
				const sourceOrder = aiSuggestionsShown ? source.index - 1 : source.index;
				const destinationOrder = aiSuggestionsShown ? destination.index - 1 : destination.index;

				const optimisticGroups = groups.map(group => {
					// Skip the AI suggestions group in optimistic updates
					if (isGroupSyntheticAISuggestions(group)) {
						return { id: group.id, order: group.order };
					}

					let { order } = group;
					if (order === sourceOrder) order = destinationOrder;
					else if (order > sourceOrder && order <= destinationOrder) order -= 1;
					else if (order < sourceOrder && order >= destinationOrder) order += 1;
					return { id: group.id, order: order };
				});

				commitMutation<ActionItemGroupsListMoveMutation>(environment, {
					mutation: moveActionItemGroupMutation,
					variables: {
						groupId: draggableId,
						newOrder: destinationOrder,
					},
					optimisticResponse: { moveCustomActionItemGroup: { actionItemGroups: optimisticGroups } },
				});
				return;
			}

			// Moving blocks within groups is only available when sorting is CUSTOM
			// And we don't wanna move if nothing will be changed
			if (
				destination.droppableId === source.droppableId &&
				(sortBy !== SORT_BY_CUSTOM || destination.index === source.index)
			) {
				return;
			}

			const movedToGroupIndex = groupsDictionary[destination.droppableId];
			if (movedToGroupIndex === undefined) {
				console.error("Moved to an nonexistent group, this should never happen");
				return;
			}

			// Adjust index to account for AI suggestions group at array position 0
			const actualGroupIndex = aiSuggestionsShown ? movedToGroupIndex + 1 : movedToGroupIndex;

			if (actualGroupIndex >= groups.length) {
				console.error("Moved to an nonexistent group, this should never happen");
				return;
			}

			// We push the items downwards if we are moving to a different group
			// Or if we are moving upwards
			const pushingDownwards = destination.droppableId !== source.droppableId || destination.index < source.index;

			const movedToGroup = groups[actualGroupIndex];
			let orderInsertedBefore: string | undefined;
			let orderInsertedAfter: string | undefined;

			if (sortBy === SORT_BY_CUSTOM) {
				if (!pushingDownwards) {
					if (destination.index < movedToGroup.items.edges.length - 1) {
						orderInsertedBefore =
							movedToGroup.items.edges[destination.index + 1]?.node.userOrderWithinGroup ?? undefined;
					}
					if (destination.index < movedToGroup.items.edges.length) {
						orderInsertedAfter = movedToGroup.items.edges[destination.index]?.node.userOrderWithinGroup ?? undefined;
					}
				} else {
					if (destination.index < movedToGroup.items.edges.length) {
						orderInsertedBefore = movedToGroup.items.edges[destination.index]?.node.userOrderWithinGroup ?? undefined;
					}
					if (destination.index > 0) {
						orderInsertedAfter =
							movedToGroup.items.edges[destination.index - 1]?.node.userOrderWithinGroup ?? undefined;
					}
				}
			} else {
				// If sorting is not custom we always want to add to the top of the list
				orderInsertedAfter =
					movedToGroup.items.edges.length > 0
						? (movedToGroup.items.edges[0]?.node.userOrderWithinGroup ?? undefined)
						: undefined;
			}
			if (selectedActionItems.length > 1) {
				multiBlockMove(selectedActionItems, destination.droppableId, orderInsertedBefore, orderInsertedAfter);
				return;
			}
			const newOrder = between(orderInsertedBefore || " ", orderInsertedAfter || nowUtcMySqlFormat());
			let optimisticGroups = groups.map(group => {
				return {
					id: group.id,
					items: {
						edges: group.items.edges.map(edge => {
							return {
								node: {
									id: edge?.node.id || "",
								},
							};
						}),
					},
				};
			});
			// If moving between groups we have to change the optimistic groups
			if (destination.droppableId !== source.droppableId) {
				const addedEdge = {
					node: {
						id: draggableId,
					},
				};
				optimisticGroups = optimisticGroups.map(optimisticGroup => {
					// If this is the group that we are dropping into we need to add the item
					if (optimisticGroup.id === destination.droppableId) {
						const edges = [...optimisticGroup.items.edges, addedEdge];
						return {
							...optimisticGroup,
							items: {
								edges,
							},
						};
					}
					// If this is the group that we are dropping from we need to remove the item
					if (optimisticGroup.id === source.droppableId) {
						optimisticGroup.items.edges.splice(source.index, 1);
						return optimisticGroup;
					}
					return optimisticGroup;
				});
			}

			// Moving action item
			commitMutation<ActionItemGroupsListMoveActionItemMutation>(environment, {
				mutation: moveActionItemMutation,
				variables: {
					noteBlockId: draggableId,
					groupId: destination.droppableId,
					orderInsertedBefore,
					orderInsertedAfter,
					includeCompleted: showCompleted,
					includeWontDo: showWontDo,
					includeAiDetected: showAIDetected,
				},
				optimisticResponse: {
					moveActionItemToGroup: {
						updatedBlock: {
							id: draggableId,
							userOrderWithinGroup: newOrder,
						},
						actionItemGroups: optimisticGroups,
					},
				},
			});
		},
		[
			groups,
			groupsDictionary,
			sortBy,
			groupBy,
			selectedActionItems,
			multiBlockMove,
			showCompleted,
			showWontDo,
			showAIDetected,
			aiSuggestionsShown,
		],
	);

	return (
		<>
			<ActionItemGroupsRenderer groups={groups || []} onDropEnd={onDropEnd} />
			{!inPanel && !inRecording && <CreateActionItemGroupButton order={groups?.length || 0} />}
		</>
	);
}
